import { MutableRefObject } from 'react';
import dayjs, { type ConfigType, type Dayjs } from 'dayjs';
import {
  attempt,
  concat,
  cond,
  constant,
  difference,
  differenceBy,
  every,
  filter,
  flatMap,
  isEmpty,
  isError,
  map,
  negate,
  orderBy,
  partition,
  size,
  slice,
  sortBy,
  stubTrue,
  uniqBy,
} from 'lodash';

import { GridApiPro, type GridRowId } from '@mui/x-data-grid-pro';

import { AuthUser, Country } from '@libraries/api/gen-api';
import { AdminProduct } from '@libraries/api/gen-api-admin/api';

import { AllRoles } from '../types/types';

import { isServiceConsole, isStandardProduct } from './helpers';

export const isNotEmpty = negate(isEmpty);
export const isNotError = negate(isError);

export const succeeds =
  (fn: (...args: any[]) => void) =>
  (...args: any[]) =>
    isNotError(attempt(fn, args));

type Predicate = (...args: any[]) => boolean;

export const allPass =
  (predicates: Array<Predicate>) =>
  (...args: any[]) =>
    every(predicates, (predicate) => predicate(args));

export const anyPass =
  (...predicates: Array<Predicate>) =>
  (...args: any[]) => {
    for (const predicate of predicates) {
      if (predicate(...args)) {
        return true;
      }
    }

    return false;
  };

export const isValidJson = allPass([succeeds(JSON.parse)]);

export const safeParseJson = <TOut>(value: string) =>
  cond<string, TOut>([
    [isValidJson, JSON.parse],
    [stubTrue, constant(undefined)],
  ])(value);

export const getAuthKey = (user: AuthUser): string => {
  const { id, org } = user;
  return `${isServiceConsole ? 'service' : 'enterprise'}:${id}:${org}-view`;
};

export function setDefaultRoleToStorage(user: AuthUser, role: AllRoles): void {
  const { id, org } = user;
  if (isEmpty(user) || isEmpty(org) || isEmpty(id)) {
    return;
  }
  const scopedKey = getAuthKey(user);
  localStorage.setItem(scopedKey, JSON.stringify(role));
}

export function getDefaultRoleFromStorage(user: AuthUser): string {
  const scopedKey = getAuthKey(user);
  return safeParseJson(localStorage.getItem(scopedKey));
}

export function removeDefaultRoleFromStorage(user: AuthUser) {
  const scopedKey = getAuthKey(user);
  localStorage.removeItem(scopedKey);
}

export const getDefaultRole = (user: AuthUser): string => {
  const { org, orgs } = user;
  const defaultRoles = orgs?.[org]?.roles;
  const defaultRole = getDefaultRoleFromStorage(user);
  const role = defaultRole && defaultRoles.includes(defaultRole) ? defaultRole : defaultRoles[0];
  if (isEmpty(defaultRole) || role !== defaultRole) {
    setDefaultRoleToStorage(user, role as AllRoles);
  }
  return role;
};

export const getExcludedShipmentCsvColumns = (
  gridApiRef: MutableRefObject<GridApiPro>,
  excludedColumns: string[] = []
): string[] | undefined => {
  const allColumns = gridApiRef.current?.getAllColumns();
  const visibleCols = gridApiRef.current?.getVisibleColumns();
  const shipmentAddressRelatedFields = [
    'street_line1',
    'street_line2',
    'street_line3',
    'city',
    'region',
    'postal_code',
  ];

  const hiddenColumnObjects = differenceBy(allColumns, visibleCols, 'field');
  const hiddenColumnFields = difference(map(hiddenColumnObjects, 'field'), excludedColumns);
  const addressIndex = hiddenColumnFields.indexOf('shipment_address');

  if (addressIndex > -1) {
    hiddenColumnFields.splice(addressIndex, 1, ...shipmentAddressRelatedFields);
  }

  return isEmpty(hiddenColumnFields) ? undefined : hiddenColumnFields;
};

export function organizeCountries(countries: Array<Country>) {
  return flatMap(
    partition(countries, (country) => country.country_code_2 === 'US'),
    (countryArray) => orderBy(countryArray, ['country_name'], ['asc'])
  );
}

/**
 * Given the current page (0-based), the number of rows per page, and the number of rows, returns the number of empty rows to render to fill the empty space of the table
 */
export function getRowFiller(page: number, rowsPerPage: number, rows: number) {
  return Math.max(0, (1 + page) * rowsPerPage - rows);
}

/**
 * Get a page of data given the current page, number of rows per page, and an array of rows
 */
export function getPage<TRow>(page: number, rowsPerPage: number, rows: Array<TRow>) {
  return slice(rows, page * rowsPerPage, page * rowsPerPage + rowsPerPage);
}

/**
 * Given a list of IDs, and an ID, adds the ID to the list of IDs if it doesn't exist, otherwise it's removed from the list
 */
export function reconcileExpandedRows(expandedRowIds: Array<GridRowId>, rowId: GridRowId) {
  const [foundRowId, filteredRows] = partition(expandedRowIds, (id) => id === rowId);

  const includeRowId = size(foundRowId) === 0;

  return concat(filteredRows, includeRowId ? [rowId] : []);
}

/** Converts a date, string, Dayjs, or Dayjs.ConfigType to Dayjs. Dates are converted to UTC for Service Console users and the user's timezone otherwise. */
export function toDayjs(date: string | Date | Dayjs | ConfigType): Dayjs {
  if (date === null) {
    return null;
  }

  return isServiceConsole ? dayjs.utc(date) : dayjs(date);
}

/** *
 * getProduct images dynamic
 */

export function getProductImages(products: AdminProduct[], isVirtual?: boolean) {
  return uniqBy(
    sortBy(
      filter(
        products,
        (p: AdminProduct) => (isVirtual ? p.is_virtual_product : isStandardProduct(p)) && !p.customization_id
      ),
      'product_id'
    ),
    'product_image'
  );
}

// A util to prevent non number entry into textfield type 'number'
export const handleKeyDownForNumbersOnly = (e: { key: string; preventDefault: () => void }) => {
  const disallowedKeys = ['e', 'E', '-', '+', '.'];
  if (disallowedKeys.includes(e.key)) {
    e.preventDefault();
  }
};
